import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { Focusable } from '@medialib/styleguide'
import { useLanguage } from '../useLanguage'
import { SHELL_NS } from '../i18n'

// Short labels for the header chips. The switch shows codes rather than endonyms
// so the control stays compact in the header row; the `aria-label` carries the
// human meaning for assistive tech.
const LANGUAGE_LABELS: Record<string, string> = {
  en: 'EN',
  fr: 'FR',
  de: 'DE'
}

/**
 * Header language switcher. It belongs to the shell, not a pilet, because the
 * active language is shell-owned state (useLanguage → persisted bucket → shared
 * i18next); flipping it re-renders every pilet in the new locale.
 *
 * Rendered as buttons rather than a native <select> so the TV remote lands on
 * each option as its own spatial-navigation stop (each wrapped in Focusable, the
 * same pattern the other header actions use).
 */
const LanguageSwitcher: React.FC = () => {
  const { language, setLanguage, languages } = useLanguage()
  const { t } = useTranslation(SHELL_NS)

  return (
    <div className='LanguageSwitcher' role='group' aria-label={t('language.label')} data-dismiss-ignore=''>
      {languages.map(lng => (
        <Focusable key={lng} className='mlsg-focusable--inline'>
          <button
            type='button'
            className='LanguageSwitcher__option'
            aria-pressed={lng === language}
            data-active={lng === language}
            onClick={() => setLanguage(lng)}
          >
            {LANGUAGE_LABELS[lng] ?? lng.toUpperCase()}
          </button>
        </Focusable>
      ))}
    </div>
  )
}

export default LanguageSwitcher
